import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Brain, CheckCircle2, XCircle, Zap, RotateCcw, BookOpen, Clock, Star } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export default function Review({ api }) {
  const [stats, setStats] = useState(null);
  const [dueCards, setDueCards] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [sessionXp, setSessionXp] = useState(0);
  const [sessionCorrect, setSessionCorrect] = useState(0);
  const [sessionTotal, setSessionTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const fetchData = async () => {
    try {
      const [statsRes, dueRes] = await Promise.all([
        axios.get(`${api}/reviews/stats`, { withCredentials: true }),
        axios.get(`${api}/reviews/due`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setDueCards(dueRes.data.cards || []);
    } catch {
      toast.error("Failed to load reviews");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [api]);

  const startReview = () => {
    setReviewing(true);
    setCurrentIdx(0);
    setShowAnswer(false);
    setSelectedAnswer(null);
    setSessionXp(0);
    setSessionCorrect(0);
    setSessionTotal(0);
    setShowSummary(false);
  };

  const handleAnswer = (optionIdx) => {
    setSelectedAnswer(optionIdx);
    setShowAnswer(true);
  };

  const submitReview = async (quality) => {
    const card = dueCards[currentIdx];
    if (!card) return;
    try {
      const res = await axios.post(`${api}/reviews/${card.id}/answer`, { quality }, { withCredentials: true });
      setSessionXp((prev) => prev + res.data.xp_earned);
      setSessionTotal((prev) => prev + 1);
      if (res.data.correct) setSessionCorrect((prev) => prev + 1);
    } catch {
      toast.error("Failed to submit review");
    }
    // Move to next card
    if (currentIdx < dueCards.length - 1) {
      setCurrentIdx((prev) => prev + 1);
      setShowAnswer(false);
      setSelectedAnswer(null);
    } else {
      setShowSummary(true);
    }
  };

  const currentCard = dueCards[currentIdx];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="review-loading">
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] text-center p-8">
          <div className="w-12 h-12 border-4 border-neo-purple border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="font-heading font-bold text-lg dark:text-white">Loading review cards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="review-page">
      <div className="text-center">
        <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight dark:text-white">
          Spaced Review
        </h1>
        <p className="text-neo-text-secondary dark:text-gray-400 mt-2">
          Reinforce what you've learned with spaced repetition
        </p>
      </div>

      {/* Stats Overview */}
      {stats && !reviewing && (
        <div className="grid grid-cols-3 gap-4">
          <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-4">
            <div className="w-10 h-10 rounded-lg bg-neo-purple border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2">
              <Brain className="w-5 h-5 text-neo-text" />
            </div>
            <p className="font-heading font-extrabold text-2xl dark:text-white" data-testid="total-cards">{stats.total_cards}</p>
            <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Total Cards</p>
          </div>
          <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-4">
            <div className="w-10 h-10 rounded-lg bg-neo-red border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <p className="font-heading font-extrabold text-2xl dark:text-white" data-testid="due-today">{stats.due_today}</p>
            <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Due Today</p>
          </div>
          <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-4">
            <div className="w-10 h-10 rounded-lg bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-2">
              <Star className="w-5 h-5 text-neo-text" />
            </div>
            <p className="font-heading font-extrabold text-2xl dark:text-white" data-testid="mastered-cards">{stats.mastered}</p>
            <p className="text-xs uppercase tracking-[0.15em] text-neo-text-secondary dark:text-gray-400 font-semibold">Mastered</p>
          </div>
        </div>
      )}

      {/* Start Review or No Cards */}
      {!reviewing && !showSummary && (
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-8" data-testid="review-start">
          {dueCards.length > 0 ? (
            <>
              <Brain className="w-16 h-16 text-neo-purple mx-auto mb-4" />
              <h2 className="font-heading font-extrabold text-2xl dark:text-white mb-2">
                {dueCards.length} Card{dueCards.length > 1 ? "s" : ""} Due for Review
              </h2>
              <p className="text-neo-text-secondary dark:text-gray-400 mb-6">
                Earn +10 XP per correct review. Strengthen your memory!
              </p>
              <button
                onClick={startReview}
                className="neo-btn neo-btn-primary text-lg px-8 py-3"
                data-testid="start-review-btn"
              >
                Start Review Session
              </button>
            </>
          ) : stats && stats.total_cards > 0 ? (
            <>
              <CheckCircle2 className="w-16 h-16 text-neo-green mx-auto mb-4" />
              <h2 className="font-heading font-extrabold text-2xl dark:text-white mb-2">All Caught Up!</h2>
              <p className="text-neo-text-secondary dark:text-gray-400">
                No cards due for review right now. Keep reading to add more cards!
              </p>
            </>
          ) : (
            <>
              <Brain className="w-16 h-16 text-neo-text-secondary dark:text-gray-500 mx-auto mb-4" />
              <h2 className="font-heading font-extrabold text-2xl dark:text-white mb-2">No Review Cards Yet</h2>
              <p className="text-neo-text-secondary dark:text-gray-400 mb-2">
                Complete quizzes while reading papers to generate review cards.
              </p>
              <p className="text-sm text-neo-text-secondary dark:text-gray-400">
                Cards are created automatically from your quiz questions.
              </p>
            </>
          )}
        </div>
      )}

      {/* Active Review */}
      {reviewing && currentCard && !showSummary && (
        <div className="space-y-4">
          {/* Progress */}
          <div className="flex items-center justify-between text-sm dark:text-gray-300">
            <span className="font-heading font-bold">Card {currentIdx + 1} of {dueCards.length}</span>
            <div className="flex items-center gap-2">
              <span className="neo-badge bg-neo-yellow text-xs"><Zap className="w-3 h-3" /> +{sessionXp} XP</span>
              <span className="text-neo-text-secondary dark:text-gray-400">{sessionCorrect}/{sessionTotal} correct</span>
            </div>
          </div>
          <div className="neo-progress">
            <div className="neo-progress-fill" style={{ width: `${((currentIdx + 1) / dueCards.length) * 100}%` }} />
          </div>

          {/* Card */}
          <motion.div
            key={currentCard.id}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444]"
            data-testid="review-card"
          >
            <p className="text-xs text-neo-text-secondary dark:text-gray-400 mb-2 flex items-center gap-1">
              <BookOpen className="w-3 h-3" /> {currentCard.paper_title}
            </p>
            <p className="font-heading font-bold text-lg mb-4 dark:text-white">{currentCard.question}</p>

            <div className="space-y-2">
              {currentCard.options?.map((opt, oi) => {
                const isCorrect = oi === currentCard.correct_index;
                const isSelected = selectedAnswer === oi;
                let style = "border-neo-border dark:border-[#444] hover:border-neo-yellow/50";
                if (showAnswer) {
                  if (isCorrect) style = "border-neo-green bg-neo-green/20";
                  else if (isSelected) style = "border-neo-red bg-neo-red/20";
                  else style = "border-gray-200 dark:border-gray-600 opacity-60";
                } else if (isSelected) {
                  style = "border-neo-yellow bg-neo-yellow/20 shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444]";
                }
                return (
                  <button
                    key={oi}
                    onClick={() => !showAnswer && handleAnswer(oi)}
                    disabled={showAnswer}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all dark:text-gray-200 ${style}`}
                    data-testid={`review-option-${oi}`}
                  >
                    <span className="text-sm flex items-center gap-2">
                      {showAnswer && isCorrect && <CheckCircle2 className="w-4 h-4 text-neo-green flex-shrink-0" />}
                      {showAnswer && isSelected && !isCorrect && <XCircle className="w-4 h-4 text-neo-red flex-shrink-0" />}
                      {opt}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* After revealing answer */}
            {showAnswer && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mt-4 pt-4 border-t-2 border-neo-border dark:border-[#444]"
              >
                <p className="text-sm font-bold mb-3 dark:text-white">
                  {selectedAnswer === currentCard.correct_index ? (
                    <span className="text-neo-green flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Correct!</span>
                  ) : (
                    <span className="text-neo-red flex items-center gap-1"><XCircle className="w-4 h-4" /> Incorrect</span>
                  )}
                </p>
                <p className="text-xs text-neo-text-secondary dark:text-gray-400 mb-3">How well did you know this?</p>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => submitReview(1)}
                    className="neo-btn neo-btn-danger text-xs py-2"
                    data-testid="review-again-btn"
                  >
                    <RotateCcw className="w-3 h-3 mr-1" /> Again
                  </button>
                  <button
                    onClick={() => submitReview(3)}
                    className="neo-btn bg-neo-yellow text-neo-text text-xs py-2"
                    data-testid="review-good-btn"
                  >
                    Good
                  </button>
                  <button
                    onClick={() => submitReview(5)}
                    className="neo-btn neo-btn-success text-xs py-2"
                    data-testid="review-easy-btn"
                  >
                    <Star className="w-3 h-3 mr-1" /> Easy
                  </button>
                </div>
              </motion.div>
            )}
          </motion.div>
        </div>
      )}

      {/* Session Summary */}
      {showSummary && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-8"
          data-testid="review-summary"
        >
          <div className="w-16 h-16 rounded-full bg-neo-green border-2 border-neo-border shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-neo-text" />
          </div>
          <h2 className="font-heading font-extrabold text-2xl dark:text-white mb-2">Review Complete!</h2>
          <div className="grid grid-cols-3 gap-4 my-6 max-w-xs mx-auto">
            <div>
              <p className="font-heading font-extrabold text-2xl dark:text-white">{sessionCorrect}</p>
              <p className="text-xs text-neo-text-secondary dark:text-gray-400">Correct</p>
            </div>
            <div>
              <p className="font-heading font-extrabold text-2xl dark:text-white">{sessionTotal}</p>
              <p className="text-xs text-neo-text-secondary dark:text-gray-400">Reviewed</p>
            </div>
            <div>
              <p className="font-heading font-extrabold text-2xl text-neo-yellow">{sessionXp}</p>
              <p className="text-xs text-neo-text-secondary dark:text-gray-400">XP</p>
            </div>
          </div>
          <button
            onClick={() => { setReviewing(false); setShowSummary(false); fetchData(); }}
            className="neo-btn neo-btn-primary px-8 py-3"
            data-testid="review-done-btn"
          >
            Done
          </button>
        </motion.div>
      )}
    </div>
  );
}
